import { Moment } from "moment"
import React, { useContext, useEffect, useState } from "react"
import { Line, Bar } from "react-chartjs-2"
import { CategoryScale } from "chart.js"
import Chart from "chart.js/auto"
import { formatMoney } from "~/utils/formatMoney"
import { AdminContext } from "~/contexts/AdminContext"
import { formatToNearestDollar } from "../../../utils/formatMoney"
import Slider from "react-slick"
import useBreakpoint from "~/hooks/useBreakpoint"

Chart.register(CategoryScale)

interface ComponentProps {
  sitesOccupancyData: Array<any> // Specify the type argument for the Array type
  reservationsVolumeData: Array<any> // Specify the type argument for the Array type
  startDate: Moment
  endDate: Moment
  numberOfSites: number
  isLoading: boolean
}

const ChartsGrid = ({ sitesOccupancyData, reservationsVolumeData, isLoading }: ComponentProps) => {
  let dateLabels = []
  const [labels, setLabels] = useState<any>([])
  const [reservationVolumeValues, setReservationVolumeValues] = useState<any>([])
  const [occupancyValues, setOccupancyValues] = useState<any>([])
  const { camp } = useContext(AdminContext)
  const isMedium = useBreakpoint("(max-width: 768px)")

  useEffect(() => {
    setLabels(dateLabels)
    setReservationVolumeValues(reservationsVolumeData)
    setOccupancyValues(sitesOccupancyData)
  }, [sitesOccupancyData, reservationsVolumeData])

  let reservationVolumeData = {
    labels: labels,
    datasets: [
      {
        label: "Reservation volume",
        data: reservationVolumeValues ? reservationVolumeValues["data"] : [],
        fill: false,
        borderColor: "rgba(5, 150, 105)",
        tension: 0.1,
      },
    ],
  }

  const reservationVolumeOptions = {
    scales: {
      y: {
        ticks: {
          callback: function (value) {
            return formatToNearestDollar(value, camp.currency)
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || ""

            if (label) {
              label += ": "
            }
            if (context.parsed.y !== null) {
              label += formatMoney(context.parsed.y, camp.currency)
            }
            return label
          },
        },
      },
    },
  }

  let occupancyData = {
    labels: labels,
    datasets: [
      {
        label: "Overall Occupancy",
        data: occupancyValues ? occupancyValues["data"] : [],
        fill: true,
        backgroundColor: "rgba(5, 150, 105, .7)",
        tension: 0.1,
      },
    ],
  }
  const occupancyOptions = {
    scales: {
      y: {
        ticks: {
          callback: function (value) {
            return value + "%"
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || ""

            if (label) {
              label += ": "
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(1) + "%"
            }
            return label
          },
        },
      },
    },
  }

  const settings = {
    dots: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  }

  const chartItems = [
    <div key="chart1" className="w-full flex flex-col justify-start">
      <div className="text-xl font-bold mb-2">Total Reservation Volume</div>
      <div className="text-2xl font-normal mb-5 text-gray-500">
        {isLoading || !reservationsVolumeData ? (
          <div className="animate-pulse bg-gray-200 h-6 w-32 rounded"></div>
        ) : (
          reservationsVolumeData && formatMoney(reservationsVolumeData["summary"]["total_volume"], camp?.currency)
        )}
      </div>
      <Line data={reservationVolumeData} options={reservationVolumeOptions} style={{ maxHeight: "550px" }} />
    </div>,
    <div key="chart2" className="w-full flex flex-col justify-end">
      <div className="text-xl font-bold mb-2">Average occupancy</div>
      <div className="text-2xl font-normal mb-5 text-gray-500">
        {isLoading || !sitesOccupancyData ? (
          <div className="animate-pulse bg-gray-200 h-6 w-20 rounded"></div>
        ) : (
          `${sitesOccupancyData && sitesOccupancyData?.["summary"]?.["average_occupancy"]}%`
        )}
      </div>
      <Bar data={occupancyData} options={occupancyOptions} style={{ maxHeight: "550px" }} />
    </div>,
  ]

  return (
    <div className="pt-4">
      {isMedium ? (
        <Slider {...settings}>
          {chartItems.map((item) => (
            <div key={item.key}>{item}</div>
          ))}
        </Slider>
      ) : (
        <div className="flex justify-between md:flex-row gap-8 w-full">{chartItems}</div>
      )}
    </div>
  )
}

export default ChartsGrid
